import React from "react";
import { AlertTriangle } from "lucide-react";
import Modal from "../uiComponents/Modal";
import PromptModal from "../uiComponents/PromptModal";
import TagInput from "../uiComponents/TagInput";
import { ChipGroup } from "../uiComponents/Chip";
import type { useBrandSettingsModal } from "../../hooks/useBrandSettingsModal";
import "./brandSettingsModal.css";

type BrandSettingsModalProps = ReturnType<typeof useBrandSettingsModal>;

const BrandSettingsModal: React.FC<BrandSettingsModalProps> = ({
  isOpen,
  showDeleteConfirm,
  isDeleting,
  brandName,
  setBrandName,
  keyCompetitors,
  setKeyCompetitors,
  bannedWords,
  setBannedWords,
  brandTone,
  setBrandTone,
  toneOptions,
  closeSettings,
  handleSave,
  handleDeleteClick,
  handleConfirmDelete,
  closeDeleteConfirm,
  handleAddCustomTone,
  showCustomToneModal,
  closeCustomToneModal,
  handleCustomToneSubmit,
}) => {
  return (
    <>
      <PromptModal
        isOpen={showCustomToneModal}
        onClose={closeCustomToneModal}
        title="Custom tone"
        message="Enter a custom brand tone:"
        placeholder="e.g. Bold, Casual"
        onSubmit={handleCustomToneSubmit}
        submitLabel="Add"
      />
      <Modal
        isOpen={isOpen}
        onClose={closeSettings}
        title="Settings"
        size="large"
        closeOnOverlayClick={true}
        footer={
          <div className="brand-settings-modal-footer">
            <button
              type="button"
              className="brand-settings-btn brand-settings-btn-cancel"
              onClick={closeSettings}
            >
              Cancel
            </button>
            <button
              type="button"
              className="brand-settings-btn brand-settings-btn-save"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        }
      >
        <div className="brand-settings-modal-body">
          <div className="brand-settings-field">
            <label className="brand-settings-label">Brand name</label>
            <input
              type="text"
              className="brand-settings-input"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder="e.g. Acme Innovations"
            />
          </div>

          <div className="brand-settings-field">
            <TagInput
              label="Key competitors"
              value={keyCompetitors}
              onChange={setKeyCompetitors}
              placeholder="Add a competitor..."
              helperText="Competitors you want to monitor or compare against."
            />
          </div>

          <div className="brand-settings-field">
            <TagInput
              label="Banned words"
              value={bannedWords}
              onChange={setBannedWords}
              placeholder="Add a word..."
              helperText="Words the AI should never use in drafts."
            />
          </div>

          <div className="brand-settings-field">
            <ChipGroup
              label="Brand tone"
              options={toneOptions}
              value={brandTone}
              onChange={(val) => setBrandTone(val as string)}
              onAddCustom={handleAddCustomTone}
            />
          </div>

          <div className="brand-settings-danger-zone">
            <div className="brand-settings-danger-title">Danger Zone</div>
            <p className="brand-settings-danger-warning">
              Once you delete a brand, there is no going back. All associated
              data, drafts, and connections will be permanently removed.
            </p>
            <button
              type="button"
              className="brand-settings-btn brand-settings-btn-delete"
              onClick={handleDeleteClick}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteConfirm}
        onClose={isDeleting ? () => {} : closeDeleteConfirm}
        title="Delete brand?"
        size="small"
        closeOnOverlayClick={!isDeleting}
        footer={
          <div className="brand-settings-modal-footer">
            <button
              type="button"
              className="brand-settings-btn brand-settings-btn-cancel"
              onClick={closeDeleteConfirm}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="brand-settings-btn brand-settings-btn-delete-confirm"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className="brand-settings-delete-confirm-spinner">
                  {/* <Spinner size="small" style={{ display: "block" }} /> */}
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        }
      >
        <div className="brand-settings-delete-confirm-body">
          <AlertTriangle size={24} className="brand-settings-delete-icon" />
          <p>
            This action cannot be reversed. Are you sure you want to delete this
            brand?
          </p>
        </div>
      </Modal>
    </>
  );
};

export default BrandSettingsModal;
